
import { defineComponent } from 'vue'
import { mapState, mapGetters } from 'vuex'
import { AuthActionTypes } from '@/store/modules/auth/interfaces/action-types'
import { constants } from '@/constants'
import { getValueByLabel } from './utils/permissions'
import { HelpPagesActionType } from '@/store/modules/help-pages/interfaces'
import { IAuthState } from './store/modules/auth/IAuthState'
import { LanguagesType } from '@/types'
import { LinksActionType } from '@/store/modules/links/interfaces'
import { ISearchValues } from '@/store/modules/main/intefaces/search/IFetchSearchValuesResponse'
import { setI18nLanguage } from '@/i18n'

import AppGlobalModal from '@/components/app/AppGlobalModal.vue'
import BannersComponent from '@/components/BannersComponent.vue'
import BannerSection from '@/components/homepage/BannerSection.vue'
import Error401Page from './pages/Error401Page.vue'
import HelpActionButton from '@/components/HelpActionButton.vue'
import MessageModal from '@/components/common/MessageModal.vue'
import ModalComponent from './components/ModalComponent.vue'
import ResizeObserver from '@/components/ResizeObserver.vue'
import TutorialModal from '@/components/TutorialModal.vue'
import UserToolbar from '@/components/UserToolbar.vue'
import { PORTAL_DICTIONARY } from '@/const/dictionary'
import AppMenuMixin from './mixins/appMenu'

export default defineComponent({
  name: 'App',

  components: {
    AppGlobalModal,
    BannersComponent,
    BannerSection,
    Error401Page,
    HelpActionButton,
    MessageModal,
    ModalComponent,
    ResizeObserver,
    TutorialModal,
    UserToolbar
  },

  mixins: [AppMenuMixin],

  data: () => ({
    gigICode: '',
    gigIvToken: '',
    clientWidth: 0,
    isPortalChrome: true,
    theme: constants.application.theme,
    isLoaded: false,
    openModal: false,
    message: '',
    subMessage: '',
    searchValue: '',
    searchValues: [] as ISearchValues[],
    isErrorModal: true,
    copyRightMsg: `© Copyright Ursus - ${new Date().getFullYear()}`,
    activeLanguage: (localStorage.getItem('lang') || constants.application.defaulLang) as LanguagesType,
    isAgeConfirmed: !!sessionStorage.getItem('isAgeConfirmed'),
    dictionary: PORTAL_DICTIONARY
  }),

  computed: {
    footerLinksBrands () {
      const footerBrandLinks = this.portalHardCodeLink
        .filter((data) => data.type === 'Footer Brand')
        .map((data) => ({
          label: data.label,
          link: data.url.trim(),
          target: '_blank',
          order: data.order
        }))
        .sort((a, b) => a.order - b.order)

      return this.isLoggedIn ? [{ title: 'brands' }, ...footerBrandLinks] : []
    },
    footerLinksSocial () {
      if (this.isLoggedIn) {
        const aboutUsLinks = this.portalHardCodeLink
          .filter((data) => data.type === 'Social Media')
          .map((data) => ({
            label: data.name,
            link: data.url.trim(),
            target: '_blank'
          }))

        return aboutUsLinks
      } else {
        return [] // Return an empty array if the user is not logged in
      }
    },
    footerLinks () {
      const aboutUsLinks = this.portalHardCodeLink
        .filter((data) => data.type === 'About Us')
        .map((data) => ({
          label: data.label, // Use the 'label' field from the backend as the label for about us links
          link: data.url.trim(),
          target: '_blank',
          order: data.order
        }))
        .sort((a, b) => a.order - b.order)

      return this.isLoggedIn ? [{ title: 'about' }, ...aboutUsLinks] : []
    },
    footerLinksMobile () {
      const mobileBrandsLinks = this.portalHardCodeLink
        .filter((data) => data.type === 'Mobile Brands')
        .map((data) => ({
          label: data.label, // Use the 'label' field from the backend as the label for mobile brands
          link: data.url.trim(),
          target: '_blank',
          order: data.order
        }))
        .sort((a, b) => a.order - b.order)

      const aboutUsLinks = this.portalHardCodeLink
        .filter((data) => data.type === 'About Us')
        .map((data) => ({
          label: data.label, // Use the 'label' field from the backend as the label for about us links
          link: data.url.trim(),
          target: '_blank',
          order: data.order
        }))
        .sort((a, b) => a.order - b.order)

      return [...mobileBrandsLinks, ...aboutUsLinks]
    },
    footerExternalLinks () {
      return [
        {
          label: 'ursus-breweries.ro',
          link: 'https://ursus-breweries.ro',
          target: '_blank'
        },
        {
          label: 'desprealcool.ro',
          link: 'https://desprealcool.ro',
          target: '_blank'
        }
      ]
    },
    ...mapState('main', ['pageNotFound']),
    ...mapState('basket', ['id', 'items', 'progressBarMsg', 'query']),
    ...mapState('catalog', ['wishlist']),
    ...mapGetters('basket', ['progressBarMessage']),
    ...mapState('main', ['permissions']),

    linksStore () {
      return this.$store.state.links
    },
    showSearchBar () {
      return this.checkPermission('Catalogue', {}).length !== 0
    },
    canProceed () {
      const progressBar = this.query?.progress_bar_data
      let proceedFlag = true
      if (progressBar?.iRunnerType === '') {
        proceedFlag = false
      } else if (progressBar?.iRunnerType === 'low_runner') {
        if (progressBar?.iTotalCartWeight <= 0 || progressBar?.iTotalCartWeight < progressBar?.iMinimumOrderWeight) {
          proceedFlag = false
        } else if (progressBar?.iTotalCartWeight > progressBar?.iMaximumOrderWeight) {
          proceedFlag = false
        }
      } else if (progressBar?.iRunnerType === 'high_runner') {
        if (progressBar?.iTotalCartWeight < 1) {
          proceedFlag = false
        }
      }
      return proceedFlag
    },
    messageCart () {
      return this.canProceed ? this.$t('The required quantity of goods has been reached') : ''
    },
    languages () {
      return ['ro', 'en']
    },

    menuLinks () {
      const menu = [...this.menu]

      const menuWithoutOtherLinks = []
      if (menu[0]?.children?.length > 0) {
        for (let i = 0; i < menu[0]?.children?.length; i++) {
          const element = menu[0].children[i]
          if ('isOther' in element === false) {
            menuWithoutOtherLinks.push(element)
          }
        }
      }

      return menu
    },

    total () {
      return this.$store.state.links.portalHardCodeLink.total
    },

    limit () {
      return this.$store.state.links.portalHardCodeLink.limit
    },

    computedDictionary () {
      const links = this.linksStore?.links?.reduce((acc, value, index) => {
        acc['custom-link' + (index + 1)] = value?.title
        return acc
      }, {} as Record<string, string>)

      const activeLanguage = this.activeLanguage as keyof typeof this.dictionary
      return { ...this.dictionary, [activeLanguage]: { ...this.dictionary[activeLanguage], ...links } }
    },

    isLoggedIn () {
      return this.user.isLoggedIn // && !!this.user.userValidation
    },
    notAuthorized () {
      return this.$route.meta?.requireAuthentication && !this.isLoggedIn
    },
    showFooter (): boolean {
      return !this.$route.meta?.hideFooter
    },
    cartItems () {
      return this.items?.length
    },
    favoritesItems () {
      return this.wishlist?.items_count
    },
    helpPagesStore () {
      return this.$store.state.helpPages
    },
    showProfiles (): boolean {
      return getValueByLabel(this.userMenuPermission, 'My Profile') && this.allPermission?.includes('My Profile')
    },
    selectedSoldToId () : any {
      const { permissionSchema = [] } = this.$store.state.auth.user || {}
      let data : any
      let selectedShipto = ''
      if (localStorage.getItem('shipToId') != null && localStorage.getItem('shipToId') !== '') {
        data = permissionSchema.filter((permission) => (permission.accountType === 'Ship-to' &&
        permission.shipToId === localStorage.getItem('shipToId')))
        selectedShipto = data[0]?.shipToId
      } else {
        data = permissionSchema.filter((permission) => (permission.accountType === 'Ship-to'))
        selectedShipto = data[0]?.shipToId
      }
      return selectedShipto
    }
  },

  watch: {
    isLoggedIn: {
      handler (value) {
        if (value === true) {
          this.fetchLinks()
          this.fetchPortalHardCodeLink()
        }
      }
    },
    activeLanguage: {
      handler (value, oldValue) {
        if (oldValue ? value !== oldValue : !!value) {
          this.setMagentoStore(value)
          if (this.isLoggedIn) {
            this.fetchLinks()
            this.fetchPortalHardCodeLink()
          }
        }
      },
      immediate: true
    },
    'user.country': {
      handler () {
        this.setMagentoStore(this.activeLanguage)
      },
      immediate: true
    },
    $route () {
      this.isPortalChrome = !this.$route.meta?.noPortalChrome && this.isAgeConfirmed
      const urlParams = new URLSearchParams(window.location.search)
      this.gigICode = urlParams.get('gig_i_code') || ''
      this.gigIvToken = urlParams.get('gig_i_vToken') || ''
    },
    progressBarMsg (value, oldValue) {
      if ((oldValue !== value)) {
        this.$store.commit('basket/SET_PROGRESS_BAR', value)
        return value
      }
    }
  },

  created () {
    this.$store.dispatch('main/FETCH_PERMISSIONS', { root: true })
    this.isPortalChrome = !this.$route.meta?.noPortalChrome && this.isAgeConfirmed
    const urlParams = new URLSearchParams(window.location.search)
    this.gigICode = urlParams.get('gig_i_code') || ''
    this.gigIvToken = urlParams.get('gig_i_vToken') || ''
  },

  async mounted () {
    this.$nextTick(function () {
      window.gigya.hasSession().then(this.checkSession)
      window.gigya.socialize.addEventHandlers({
        onLogin: async (res: IAuthState) => {
          this.updateUserInfo(res)
          await this.$store.dispatch('main/FETCH_PERMISSIONS', { root: true })
          localStorage.setItem('shipToId', '')
          await this.$store.dispatch('basket/CREATE_EMPTY_CART', { root: true })
          await this.getFirstAvailableDate()
        }
      })
    })
  },

  methods: {
    // menu check
    async dispatchHelpPages () {
      await this.$store.dispatch(HelpPagesActionType.FETCH_ALL_HELP_PAGES)
    },
    openTutorialModal () {
      if (
        !(this.helpPagesStore !== undefined && this.helpPagesStore.helpPages && this.helpPagesStore.helpPages.length)
      ) {
        this.dispatchHelpPages()
      }
      (this.$refs.tutorialModal as InstanceType<typeof ModalComponent>).open()
    },
    closeTutorialModal () {
      (this.$refs.tutorialModal as InstanceType<typeof ModalComponent>).close()
    },
    menuNavigate (item: CustomEvent) {
      if (
        item.detail?.name === 'eLearning' ||
        item.detail?.name === 'pubMenu' ||
        (item.detail?.name === 'emptiesManagement')
      ) {
        this.$gtm.trackEvent({
          event: item.detail?.name + '_click'
        })
        window.gigya.fidm.saml.initSSO({
          spName: item.detail?.name,
          redirectURL: item.detail.href
        })
        return
      } else if (/^https?:\/\//i.test(item.detail.href) || /^https?:\/\//i.test(item.detail)) {
        const link = item.detail.href ? item.detail.href : item.detail
        window.open(link, '_blank')
        return
      }

      this.$navigate(item)
    },

    changeLanguage (langCode: LanguagesType) {
      this.activeLanguage = langCode
      setI18nLanguage(langCode)
    },

    fetchLinks () {
      this.$store.dispatch(LinksActionType.FETCH_ALL_LINKS)
    },

    /**
     * Method to update logged in user info into vuex store
     * @param res
     */
    async updateUserInfo (res: IAuthState) {
      sessionStorage.setItem('uid', res.UID) // This is for Google Analytics
      this.$store.commit('SET_AUTH_USER', res)
      await this.$store.dispatch(AuthActionTypes.GET_USER_INFO, res.UID).then(() => {
        if (localStorage.getItem('lang') !== null) {
          this.activeLanguage = (localStorage.getItem('lang') ||
          constants.application.defaulLang) as LanguagesType
          this.setMagentoStore(this.activeLanguage)
        }
        if (!this.user.userValidation && !this.$route.meta?.isUserVerification) {
          this.userLogout()
        }
      })
      if (this.user.firstLogin === true && this.user.userValidation) {
        this.openTutorialModal()
      }
    },
    /**
     * Method to initiate logout action
     * on click action of logout button
     */
    userLogout () {
      localStorage.clear()
      sessionStorage.removeItem('uid')
      const params = {
        UID: this.user.UID,
        callback: this.handleLogout
      }
      window.gigya.accounts.logout(params)
    },
    /**
     * Method to perform action after user logs out
     * Navigate user to home page
     */
    handleLogout () {
      this.$store.dispatch('logout')
      this.$navigate('/login')
    },
    /**
     * Method to check user session if it exist
     * if it does not exist then redirect user to login page
     * @param sessionExist
     */
    async checkSession (sessionExist: boolean) {
      if (sessionExist) {
        window.gigya.socialize.getUserInfo({ callback: this.updateUserInfo })
      } else {
        if (!this.$route.meta?.isUserVerification && this.$route.meta?.requireAuthentication) {
          this.$navigate('/login')
        }
      }
      this.isLoaded = true
    },
    /**
     * Callback method on click of Profile link under top right user name
     * @param event
     */
    userEdit () {
      this.$navigate('/profile/details')
    },

    setAgeConfirmed (isAgeConfirmed: boolean) {
      this.isPortalChrome = isAgeConfirmed
      this.isAgeConfirmed = isAgeConfirmed
    },
    setMagentoStore (language: string) {
      localStorage.setItem('store', `${constants.api.graphql_store}${language}`)
    },

    async removeProductFromCart (productId: string) {
      if (this.id && productId) {
        await this.$store.dispatch('basket/REMOVE_ITEM', { cart_id: this.$store.state.basket.id, cart_item_id: productId }, { root: true })
        await this.$store.dispatch('basket/FETCH_MIN_CART_QUERY_FOR_CATALOG', { cart_id: this.$store.state.basket.id }, { root: true })
      }
    },
    async getFirstAvailableDate () {
      await this.$store.dispatch('basket/GET_FIRST_AVAILABLE_DATE', { soldToId: this.selectedSoldToId })
    }
  }
})
